import type { SubscriptionTier, BillingInterval, SubscriptionProduct } from "@prisma/client";

// Byte conversion constants
const GB = 1024n * 1024n * 1024n;
const TB = 1024n * GB;

/**
 * Tier limits configuration
 * - storageBytes: Maximum storage in bytes
 * - bandwidthBytesMonthly: Monthly bandwidth allowance (null = no bandwidth included)
 * - bandwidthBytesYearly: Yearly bandwidth allowance (full year upfront)
 * - bandwidthOveragePerGB: Cost per GB of overage in dollars (null = no overage allowed)
 * - maxResolution: Maximum video resolution
 * - maxDurationMinutes: Maximum recording duration (null = unlimited)
 * - hostedVideoLimit: Max number of hosted/embeddable videos (null = unlimited)
 * - teamMemberLimit: Recorded team-member count (not enforced in this plan)
 * - canRecord: Can record videos via the desktop app
 * - canEmbed: Can embed videos on external sites
 * - canUpload: Can upload pre-existing video files (separate from canRecord)
 * - canAnalytics: Basic analytics (plays, watch time)
 * - canAdvancedAnalytics: Heatmaps, single-user breakdown
 * - canEngagementOptimization: Pro+ engagement optimization tooling
 * - canABTesting: Pro+ A/B testing
 * - canCustomBranding: Remove Flare branding on sharing/embed page
 * - canChangeColors: Customize player/page colors (paid tiers)
 * - canChapters: Enable chapter markers (paid tiers)
 * - canSubtitles: Enable subtitles/captions (paid tiers)
 * - canExportFromEditor: Export edited video from the editor (paid tiers)
 * - canPasswordProtect: Password protect videos
 * - canDomainWhitelist: Whitelist domains for embedded videos
 */
export interface TierLimits {
  storageBytes: bigint;
  bandwidthBytesMonthly: bigint | null;
  bandwidthBytesYearly: bigint | null;
  bandwidthOveragePerGB: number | null;
  maxResolution: "1080p" | "4k";
  maxDurationMinutes: number | null;
  hostedVideoLimit: number | null;
  teamMemberLimit: number;
  canRecord: boolean;
  canEmbed: boolean;
  canUpload: boolean;
  canAnalytics: boolean;
  canAdvancedAnalytics: boolean;
  canEngagementOptimization: boolean;
  canABTesting: boolean;
  canCustomBranding: boolean;
  canChangeColors: boolean;
  canChapters: boolean;
  canSubtitles: boolean;
  canExportFromEditor: boolean;
  canPasswordProtect: boolean;
  canDomainWhitelist: boolean;
}

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  RECORDING: {
    storageBytes: 200n * GB,
    bandwidthBytesMonthly: null,
    bandwidthBytesYearly: null,
    bandwidthOveragePerGB: null,
    maxResolution: "4k",
    maxDurationMinutes: null,
    hostedVideoLimit: null,
    teamMemberLimit: 1,
    canRecord: true,
    canEmbed: false,
    canUpload: false,
    canAnalytics: false,
    canAdvancedAnalytics: false,
    canEngagementOptimization: false,
    canABTesting: false,
    canCustomBranding: false,
    canChangeColors: false,
    canChapters: false,
    canSubtitles: false,
    canExportFromEditor: false,
    canPasswordProtect: true,
    canDomainWhitelist: false,
  },
  EMBED_FREE: {
    storageBytes: 50n * GB,
    bandwidthBytesMonthly: 50n * GB,
    bandwidthBytesYearly: 50n * GB * 12n,
    bandwidthOveragePerGB: null, // hard cap, no overage
    maxResolution: "1080p",
    maxDurationMinutes: 60,
    hostedVideoLimit: 1,
    teamMemberLimit: 1,
    canRecord: false,
    canEmbed: true,
    canUpload: true,
    canAnalytics: true,
    canAdvancedAnalytics: false,
    canEngagementOptimization: false,
    canABTesting: false,
    canCustomBranding: false,
    canChangeColors: false,
    canChapters: false,
    canSubtitles: false,
    canExportFromEditor: false,
    canPasswordProtect: false,
    canDomainWhitelist: false,
  },
  EMBED_STARTER: {
    storageBytes: 200n * GB,
    bandwidthBytesMonthly: 250n * GB,
    bandwidthBytesYearly: 3000n * GB,
    bandwidthOveragePerGB: 0.25,
    maxResolution: "4k",
    maxDurationMinutes: 60,
    hostedVideoLimit: 3,
    teamMemberLimit: 1,
    canRecord: false,
    canEmbed: true,
    canUpload: true,
    canAnalytics: true,
    canAdvancedAnalytics: false,
    canEngagementOptimization: false,
    canABTesting: false,
    canCustomBranding: true,
    canChangeColors: true,
    canChapters: true,
    canSubtitles: true,
    canExportFromEditor: true,
    canPasswordProtect: true,
    canDomainWhitelist: false,
  },
  EMBED_PRO: {
    storageBytes: 500n * GB,
    bandwidthBytesMonthly: 1000n * GB,
    bandwidthBytesYearly: 12000n * GB,
    bandwidthOveragePerGB: 0.25,
    maxResolution: "4k",
    maxDurationMinutes: null,
    hostedVideoLimit: 15,
    teamMemberLimit: 3,
    canRecord: false,
    canEmbed: true,
    canUpload: true,
    canAnalytics: true,
    canAdvancedAnalytics: true,
    canEngagementOptimization: true,
    canABTesting: true,
    canCustomBranding: true,
    canChangeColors: true,
    canChapters: true,
    canSubtitles: true,
    canExportFromEditor: true,
    canPasswordProtect: true,
    canDomainWhitelist: false,
  },
  EMBED_ULTRA: {
    storageBytes: 1000n * GB,
    bandwidthBytesMonthly: 3000n * GB,
    bandwidthBytesYearly: 36000n * GB,
    bandwidthOveragePerGB: 0.20,
    maxResolution: "4k",
    maxDurationMinutes: null,
    hostedVideoLimit: 50,
    teamMemberLimit: 10,
    canRecord: false,
    canEmbed: true,
    canUpload: true,
    canAnalytics: true,
    canAdvancedAnalytics: true,
    canEngagementOptimization: true,
    canABTesting: true,
    canCustomBranding: true,
    canChangeColors: true,
    canChapters: true,
    canSubtitles: true,
    canExportFromEditor: true,
    canPasswordProtect: true,
    canDomainWhitelist: true,
  },
  // --- Legacy tiers — grandfathered, identical capabilities to old TIER_LIMITS ---
  EMBED: {
    storageBytes: 200n * GB,
    bandwidthBytesMonthly: 1000n * GB,
    bandwidthBytesYearly: 12000n * GB,
    bandwidthOveragePerGB: 0.05,
    maxResolution: "4k",
    maxDurationMinutes: 60,
    hostedVideoLimit: null, // legacy: no count limit
    teamMemberLimit: 1,
    canRecord: false,
    canEmbed: true,
    canUpload: true,
    canAnalytics: true,
    canAdvancedAnalytics: false,
    canEngagementOptimization: false,
    canABTesting: false,
    canCustomBranding: true,
    canChangeColors: true,
    canChapters: true,
    canSubtitles: true,
    canExportFromEditor: true,
    canPasswordProtect: true,
    canDomainWhitelist: false,
  },
  CREATOR: {
    storageBytes: 200n * GB,
    bandwidthBytesMonthly: 1000n * GB,
    bandwidthBytesYearly: 12000n * GB,
    bandwidthOveragePerGB: 0.05,
    maxResolution: "4k",
    maxDurationMinutes: null,
    hostedVideoLimit: null,
    teamMemberLimit: 10,
    canRecord: true,
    canEmbed: true,
    canUpload: true,
    canAnalytics: true,
    canAdvancedAnalytics: true,
    canEngagementOptimization: true,
    canABTesting: true,
    canCustomBranding: true,
    canChangeColors: true,
    canChapters: true,
    canSubtitles: true,
    canExportFromEditor: true,
    canPasswordProtect: true,
    canDomainWhitelist: true,
  },
};

/**
 * Storage add-on configuration
 * - 500GB per unit
 * - €8/month or €96/year (12 × monthly, no yearly discount)
 * - Stackable (can purchase multiple)
 * Per-currency prices live in lib/mollie/pricing.json (storage key).
 */
export const STORAGE_ADDON_BYTES = 500n * GB;

/**
 * Bandwidth add-on configuration
 * - 500GB per unit (per month for monthly billing, 6000GB per year for yearly billing)
 * - €25/month or €300/year (12 × monthly, no yearly discount)
 * - Stackable
 * Per-currency prices live in lib/mollie/pricing.json (bandwidth key).
 */
export const BANDWIDTH_ADDON_BYTES_MONTHLY = 500n * GB;
export const BANDWIDTH_ADDON_BYTES_YEARLY = 6000n * GB;

/**
 * Get total storage limit including add-ons
 */
export function getTotalStorageBytes(
  tier: SubscriptionTier,
  extraStorageUnits: number
): bigint {
  const baseStorage = TIER_LIMITS[tier].storageBytes;
  const addonStorage = BigInt(extraStorageUnits) * STORAGE_ADDON_BYTES;
  return baseStorage + addonStorage;
}

/**
 * Get bandwidth limit based on tier and billing interval
 * Returns null for RECORDING tier (no bandwidth included)
 */
export function getBandwidthLimit(
  tier: SubscriptionTier,
  billingInterval: BillingInterval
): bigint | null {
  const limits = TIER_LIMITS[tier];
  if (billingInterval === "YEARLY") {
    return limits.bandwidthBytesYearly;
  }
  return limits.bandwidthBytesMonthly;
}

/**
 * Get total bandwidth limit including add-ons
 * Returns null for tiers with no bandwidth (RECORDING)
 */
export function getTotalBandwidthBytes(
  tier: SubscriptionTier,
  billingInterval: BillingInterval,
  extraBandwidthUnits: number
): bigint | null {
  const baseBandwidth = getBandwidthLimit(tier, billingInterval);
  if (baseBandwidth === null) return null;
  const addonPerUnit = billingInterval === "YEARLY"
    ? BANDWIDTH_ADDON_BYTES_YEARLY
    : BANDWIDTH_ADDON_BYTES_MONTHLY;
  return baseBandwidth + BigInt(extraBandwidthUnits) * addonPerUnit;
}

/**
 * Resolve the effective hosted-video limit for a subscription.
 *
 * A non-null `customHostedVideoLimit` on the Subscription row supersedes the
 * tier default (used for custom / enterprise comped plans). When the override
 * is null/undefined we fall back to the tier's `hostedVideoLimit`, which itself
 * may be null (unlimited — legacy EMBED / CREATOR).
 */
export function getEffectiveHostedVideoLimit(
  tier: SubscriptionTier,
  customHostedVideoLimit?: number | null
): number | null {
  if (customHostedVideoLimit != null) return customHostedVideoLimit;
  return TIER_LIMITS[tier].hostedVideoLimit;
}

/**
 * Maps each SubscriptionTier to its parent SubscriptionProduct.
 * Legacy CREATOR is mapped to RECORDING (it is a record-and-embed bundle whose
 * primary capability is recording; embed access is grandfathered alongside).
 */
export const TIER_TO_PRODUCT: Record<SubscriptionTier, SubscriptionProduct> = {
  RECORDING: "RECORDING",
  EMBED: "EMBEDS",
  CREATOR: "RECORDING",
  EMBED_FREE: "EMBEDS",
  EMBED_STARTER: "EMBEDS",
  EMBED_PRO: "EMBEDS",
  EMBED_ULTRA: "EMBEDS",
};

/**
 * Tiers that are currently sellable on the embed product line.
 *
 * EMBED_FREE is retired: no new signups, no downgrades to it. Existing
 * EMBED_FREE rows are grandfathered (TIER_LIMITS.EMBED_FREE is retained so
 * they keep full behavior), but the tier is no longer offered here.
 */
export const SELLABLE_EMBED_TIERS = [
  "EMBED_STARTER",
  "EMBED_PRO",
  "EMBED_ULTRA",
] as const;

/** Tiers that are currently sellable on the recording product line. */
export const SELLABLE_RECORDING_TIERS = ["RECORDING"] as const;

/** Legacy tiers retained only for grandfathered subscriptions. */
export const LEGACY_GRANDFATHERED_TIERS = ["EMBED", "CREATOR"] as const;
